import type { AppLocationCodec } from "@/navigation/appLocation";
import { toAbsoluteUrl } from "@/navigation/absoluteUrl";
import { NAV_KEYS } from "@/navigation/keys";

export const MISSION_DETAIL_TABS = ["timeline", "artifacts", "guardians", "channel"] as const;
export type MissionDetailTab = (typeof MISSION_DETAIL_TABS)[number];

export type MissionDetailLocation = {
  missionId: string;
  tab: MissionDetailTab;
  taskId?: string;
  focus?: string;
};

function defaults(missionId: string): MissionDetailLocation {
  return { missionId, tab: "timeline" };
}

function isTab(raw: string | null | undefined): MissionDetailTab | null {
  if (raw && (MISSION_DETAIL_TABS as readonly string[]).includes(raw)) {
    return raw as MissionDetailTab;
  }
  return null;
}

function readTaskId(params: Record<string, string | undefined>): string | undefined {
  const canonical = params[NAV_KEYS.taskId]?.trim();
  if (canonical) return canonical;
  const legacy = params[NAV_KEYS.taskLegacy]?.trim();
  return legacy || undefined;
}

function normalize(loc: MissionDetailLocation): MissionDetailLocation {
  const next: MissionDetailLocation = {
    missionId: loc.missionId,
    tab: loc.tab,
  };
  if (loc.taskId) next.taskId = loc.taskId;
  if (loc.focus) next.focus = loc.focus;
  return next;
}

function parseMissionIdFromPath(pathname: string): string | undefined {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] === "missions" && segments[1]) {
    return decodeURIComponent(segments[1]);
  }
  return undefined;
}

function fromParams(params: Record<string, string | undefined>): MissionDetailLocation {
  const missionId = params.missionId?.trim() ?? "";
  const base = defaults(missionId || "unknown");
  const tab = isTab(params[NAV_KEYS.tab]);
  if (tab) base.tab = tab;
  const taskId = readTaskId(params);
  if (taskId) base.taskId = taskId;
  const focus = params[NAV_KEYS.focus]?.trim();
  if (focus) base.focus = focus;
  if (params.missionId?.trim()) {
    base.missionId = params.missionId.trim();
  }
  return normalize(base);
}

function fromUrl(pathname: string, searchParams: URLSearchParams): MissionDetailLocation {
  const params: Record<string, string | undefined> = Object.fromEntries(searchParams);
  const pathMissionId = parseMissionIdFromPath(pathname);
  if (pathMissionId) params.missionId = pathMissionId;
  return fromParams(params);
}

function toParams(loc: MissionDetailLocation): Record<string, string> {
  const n = normalize(loc);
  const out: Record<string, string> = { missionId: n.missionId };
  if (n.tab !== "timeline") out[NAV_KEYS.tab] = n.tab;
  if (n.taskId) out[NAV_KEYS.taskId] = n.taskId;
  if (n.focus) out[NAV_KEYS.focus] = n.focus;
  return out;
}

function toUrl(loc: MissionDetailLocation): string {
  const n = normalize(loc);
  const { missionId, ...query } = toParams(n);
  const pathname = `/missions/${encodeURIComponent(missionId)}`;
  const qs = new URLSearchParams(query).toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export const missionDetailCodec: AppLocationCodec<MissionDetailLocation> = {
  appId: "mission",
  fromUrl,
  fromParams,
  toParams,
  toUrl,
};

export function buildMissionDetailWindowUrl(
  missionId: string,
  patch: Partial<Omit<MissionDetailLocation, "missionId">> = {},
): string {
  return missionDetailCodec.toUrl({ ...defaults(missionId), ...patch });
}

export function missionDetailShareUrl(
  missionId: string,
  patch: Partial<Omit<MissionDetailLocation, "missionId">> = {},
): string {
  return toAbsoluteUrl(buildMissionDetailWindowUrl(missionId, patch));
}
